import React, { useEffect, useState } from 'react';
import { ChevronLeft, ChevronRight, CheckCircle, Save } from 'lucide-react';
import { useToast } from '../../context/ToastContext';
interface Survey {
  id: number;
  title: string;
  description: string;
  closingDate: string;
  estimatedTime: string;
  anonymous: boolean;
}
interface SurveyQuestionnaireProps {
  survey: Survey;
  onClose: () => void;
}
interface Question {
  id: string;
  text: string;
  isReversed?: boolean;
}
interface Category {
  name: string;
  questions: Question[];
}
export function SurveyQuestionnaire({
  survey,
  onClose
}: SurveyQuestionnaireProps) {
  const {
    showToast
  } = useToast();
  const [currentCategoryIndex, setCurrentCategoryIndex] = useState(0);
  const [responses, setResponses] = useState<Record<string, number>>({});
  const [comments, setComments] = useState<Record<string, string>>({});
  const [isSubmitted, setIsSubmitted] = useState(false);
  // Load saved progress on mount
  useEffect(() => {
    const savedData = localStorage.getItem(`survey_progress_${survey.id}`);
    if (savedData) {
      try {
        const parsed = JSON.parse(savedData);
        setResponses(parsed.responses || {});
        setComments(parsed.comments || {});
        setCurrentCategoryIndex(parsed.currentCategoryIndex || 0);
        showToast('Progression du sondage restaurée', 'info');
      } catch (error) {
        console.error('Error loading saved survey progress:', error);
      }
    }
  }, [survey.id, showToast]);
  // Auto-save progress whenever responses or comments change
  useEffect(() => {
    if (Object.keys(responses).length > 0 || Object.keys(comments).length > 0) {
      const dataToSave = {
        responses,
        comments,
        currentCategoryIndex,
        lastSaved: new Date().toISOString()
      };
      localStorage.setItem(`survey_progress_${survey.id}`, JSON.stringify(dataToSave));
    }
  }, [responses, comments, currentCategoryIndex, survey.id]);
  const categories: Category[] = [{
    name: 'TRAVAIL',
    questions: [{
      id: 'travail_1',
      text: 'Mon travail est motivant'
    }, {
      id: 'travail_2',
      text: 'Mon travail me crée du stress',
      isReversed: true
    }, {
      id: 'travail_3',
      text: "Je sais ce que j'ai à faire dans mon travail"
    }, {
      id: 'travail_4',
      text: 'Je me sens souvent surchargé dans mon travail',
      isReversed: true
    }, {
      id: 'travail_5',
      text: 'Mon travail me satisfait'
    }, {
      id: 'travail_6',
      text: 'Je me considère autonome dans mon travail'
    }]
  }, {
    name: 'COMMUNICATION',
    questions: [{
      id: 'comm_1',
      text: "J'ai accès à toute l'information pour bien faire mon travail"
    }, {
      id: 'comm_2',
      text: "Je considère que nous investissons de l'énergie sur des projets non importants",
      isReversed: true
    }, {
      id: 'comm_3',
      text: "Il y a du commérage à l'intérieur de l'équipe",
      isReversed: true
    }, {
      id: 'comm_4',
      text: "Je connais les buts et objectifs de l'organisation"
    }, {
      id: 'comm_5',
      text: "Je sais comment contribuer aux objectifs organisationnels et stratégiques de l'organisation"
    }, {
      id: 'comm_6',
      text: 'Je suis encouragé à partager mon opinion au travail'
    }, {
      id: 'comm_7',
      text: "Je suis confortable d'aborder des sujets sur l'organisation avec mes collègues"
    }, {
      id: 'comm_8',
      text: "Je suis satisfait des moyens pour faire circuler l'information dans l'organisation"
    }, {
      id: 'comm_9',
      text: "Je travaillerais mieux si j'avais accès à plus d'information, d'assistance et/ou de conseils",
      isReversed: true
    }]
  }, {
    name: 'SALAIRE',
    questions: [{
      id: 'salaire_1',
      text: 'Mon travail est reconnu par mon équipe de travail'
    }, {
      id: 'salaire_2',
      text: 'Je suis satisfait de mon salaire'
    }, {
      id: 'salaire_3',
      text: 'Je crois que mon salaire est compétitif avec le marché'
    }, {
      id: 'salaire_4',
      text: 'Je suis satisfait de mes conditions de travail'
    }]
  }, {
    name: 'DÉVELOPPEMENT PROFESSIONNEL',
    questions: [{
      id: 'dev_1',
      text: "Je suis satisfait du processus d'évaluation de performance"
    }, {
      id: 'dev_2',
      text: 'Je suis satisfait du processus de développement des compétences et de formation'
    }, {
      id: 'dev_3',
      text: 'Je désire travailler pour mon employeur pour les 5 prochaines années'
    }, {
      id: 'dev_4',
      text: "J'ai des opportunités pour améliorer ma carrière"
    }, {
      id: 'dev_5',
      text: "J'ai les mêmes opportunités que mes collègues d'améliorer ma carrière"
    }]
  }, {
    name: 'PLANIFICATION & PRISE DE DÉCISION',
    questions: [{
      id: 'plan_1',
      text: "Je considère que l'équipe de gestion nous explique bien les plans stratégiques et les objectifs à atteindre"
    }, {
      id: 'plan_2',
      text: "J'ai des opportunités de participer à la prise de décisions"
    }, {
      id: 'plan_3',
      text: "Je trouve que la délégation est importante pour l'organisation"
    }]
  }, {
    name: "MEMBRE D'ÉQUIPE",
    questions: [{
      id: 'equipe_1',
      text: 'Si je vis une situation difficile, mes collègues me soutiendront'
    }, {
      id: 'equipe_2',
      text: 'Je considère mes collègues compétents'
    }, {
      id: 'equipe_3',
      text: "J'ai confiance en mes collègues de travail"
    }, {
      id: 'equipe_4',
      text: "Je considère avoir une bonne relation avec les membres de l'équipe"
    }, {
      id: 'equipe_5',
      text: "Je trouve que nous avons un bon esprit d'équipe"
    }, {
      id: 'equipe_6',
      text: "Je suis confortable de discuter avec tous les membres de l'équipe"
    }, {
      id: 'equipe_7',
      text: 'Les départements coopèrent bien entre eux'
    }]
  }, {
    name: 'FUTUR',
    questions: [{
      id: 'futur_1',
      text: 'Je me souci du développement futur de mon organisation'
    }, {
      id: 'futur_2',
      text: 'Je suis fier de travailler pour mon organisation'
    }, {
      id: 'futur_3',
      text: 'Je partage la même vision que mon organisation'
    }, {
      id: 'futur_4',
      text: 'Je ne sens pas le besoin de regarder pour un autre emploi'
    }, {
      id: 'futur_5',
      text: "Je cherche continuellement comment je peux m'améliorer dans mon travail"
    }]
  }, {
    name: 'SUPÉRIEUR IMMÉDIAT',
    questions: [{
      id: 'sup_1',
      text: "Donne de la rétroaction aux membres d'équipe"
    }, {
      id: 'sup_2',
      text: 'Me donne des opportunités pour résoudre mes problèmes'
    }, {
      id: 'sup_3',
      text: "Établit des objectifs pour les membres d'équipe"
    }, {
      id: 'sup_4',
      text: "Encourage les membres d'équipe à innover et partager leurs idées"
    }, {
      id: 'sup_5',
      text: "Est juste et équitable avec les membres d'équipe"
    }, {
      id: 'sup_6',
      text: "Écoute les suggestions des membres d'équipe"
    }, {
      id: 'sup_7',
      text: "Respecte les membres d'équipe"
    }, {
      id: 'sup_8',
      text: "Implique les membres d'équipe lors des changements"
    }, {
      id: 'sup_9',
      text: 'Se soucie de moi et de mes intérêts'
    }]
  }, {
    name: 'ÉQUIPE DE GESTION',
    questions: [{
      id: 'gest_1',
      text: "Donne de la rétroaction aux membres d'équipe"
    }, {
      id: 'gest_2',
      text: 'Me donne des opportunités pour résoudre mes problèmes'
    }, {
      id: 'gest_3',
      text: "Établit des objectifs pour les membres d'équipe"
    }, {
      id: 'gest_4',
      text: "Encourage les membres d'équipe à innover et partager leurs idées"
    }, {
      id: 'gest_5',
      text: "Est juste et équitable avec les membres d'équipe"
    }, {
      id: 'gest_6',
      text: "Écoute les suggestions des membres d'équipe"
    }, {
      id: 'gest_7',
      text: "Respecte les membres d'équipe"
    }, {
      id: 'gest_8',
      text: "Implique les membres d'équipe lors des changements"
    }, {
      id: 'gest_9',
      text: 'Se soucie de moi et de mes intérêts'
    }]
  }];
  const currentCategory = categories[currentCategoryIndex];
  const totalQuestions = categories.reduce((sum, cat) => sum + cat.questions.length, 0);
  const answeredQuestions = Object.keys(responses).length;
  const progressPercentage = Math.round(answeredQuestions / totalQuestions * 100);
  const handleResponse = (questionId: string, value: number) => {
    setResponses(prev => ({
      ...prev,
      [questionId]: value
    }));
  };
  const handleComment = (questionId: string, comment: string) => {
    setComments(prev => ({
      ...prev,
      [questionId]: comment
    }));
  };
  const handleSaveProgress = () => {
    const dataToSave = {
      responses,
      comments,
      currentCategoryIndex,
      lastSaved: new Date().toISOString()
    };
    localStorage.setItem(`survey_progress_${survey.id}`, JSON.stringify(dataToSave));
    showToast('Progression sauvegardée avec succès', 'success');
  };
  const handleNext = () => {
    if (currentCategoryIndex < categories.length - 1) {
      setCurrentCategoryIndex(currentCategoryIndex + 1);
    }
  };
  const handlePrevious = () => {
    if (currentCategoryIndex > 0) {
      setCurrentCategoryIndex(currentCategoryIndex - 1);
    }
  };
  const handleSubmit = () => {
    localStorage.removeItem(`survey_progress_${survey.id}`);
    setIsSubmitted(true);
    showToast('Sondage soumis avec succès. Merci pour votre participation!', 'success');
    setTimeout(() => {
      onClose();
    }, 2000);
  };
  const scaleLabels = ["Pas du tout d'accord", "Pas d'accord", 'Neutre', "D'accord", "Tout à fait d'accord"];
  if (isSubmitted) {
    return <div className="max-w-2xl mx-auto" data-id="element-1523">
        <div className="bg-white rounded-lg shadow-sm p-8 text-center" data-id="element-1524">
          <div className="mx-auto h-16 w-16 rounded-full bg-green-100 flex items-center justify-center mb-4" data-id="element-1525">
            <CheckCircle className="h-10 w-10 text-green-600" data-id="element-1526" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-1527">
            Merci pour votre participation!
          </h2>
          <p className="text-gray-600 mb-6" data-id="element-1528">
            Vos réponses ont été enregistrées de manière anonyme et
            contribueront à améliorer notre environnement de travail.
          </p>
        </div>
      </div>;
  }
  return <div className="max-w-4xl mx-auto" data-id="element-1529">
      <div className="mb-6" data-id="element-1530">
        <button onClick={onClose} className="flex items-center text-blue-600 hover:text-blue-800 mb-4" data-id="element-1531">
          <ChevronLeft className="h-4 w-4 mr-1" data-id="element-1532" />
          Retour aux sondages
        </button>
        <h1 className="text-2xl font-bold text-gray-900" data-id="element-1533">{survey.title}</h1>
        <p className="text-gray-600" data-id="element-1534">{survey.description}</p>
      </div>

      <div className="bg-white rounded-lg shadow-sm p-6 mb-6" data-id="element-1535">
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 mb-2" data-id="element-1536">
          <span className="text-sm font-medium text-gray-700" data-id="element-1537">
            Progression globale
          </span>
          <div className="flex items-center gap-3" data-id="element-1538">
            <span className="text-sm font-medium text-gray-700" data-id="element-1539">
              {answeredQuestions} / {totalQuestions} questions
            </span>
            <button onClick={handleSaveProgress} className="flex items-center gap-1 px-3 py-1.5 bg-blue-100 text-blue-700 rounded-md text-sm font-medium hover:bg-blue-200 transition-colors" data-id="element-1540">
              <Save className="h-4 w-4" data-id="element-1541" />
              Sauvegarder
            </button>
          </div>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-2.5" data-id="element-1542">
          <div className="bg-blue-600 h-2.5 rounded-full transition-all duration-300" style={{
          width: `${progressPercentage}%`
        }} data-id="element-1543"></div>
        </div>
        <p className="text-xs text-gray-500 mt-2" data-id="element-1544">
          💡 Votre progression est sauvegardée automatiquement. Vous pouvez
          reprendre le sondage plus tard.
        </p>
      </div>

      <div className="bg-white rounded-lg shadow-sm p-6 mb-6" data-id="element-1545">
        <div className="flex items-center justify-between mb-6" data-id="element-1546">
          <h2 className="text-xl font-semibold text-gray-900" data-id="element-1547">
            {currentCategory.name}
          </h2>
          <span className="text-sm text-gray-600" data-id="element-1548">
            Section {currentCategoryIndex + 1} / {categories.length}
          </span>
        </div>

        <div className="space-y-8" data-id="element-1549">
          {currentCategory.questions.map((question, index) => <div key={question.id} className="border-b border-gray-200 pb-6 last:border-b-0" data-id="element-1550">
              <div className="flex items-start mb-4" data-id="element-1551">
                <span className="flex-shrink-0 h-6 w-6 rounded-full bg-blue-100 text-blue-800 flex items-center justify-center text-sm font-medium mr-3" data-id="element-1552">
                  {index + 1}
                </span>
                <p className="text-gray-900 font-medium" data-id="element-1553">
                  {question.text}
                  {question.isReversed && <span className="ml-2 text-xs text-gray-500" data-id="element-1554">
                      (inversé)
                    </span>}
                </p>
              </div>
              <div className="ml-9" data-id="element-1555">
                <div className="grid grid-cols-5 gap-2 mb-4" data-id="element-1556">
                  {[1, 2, 3, 4, 5].map(value => <button key={value} onClick={() => handleResponse(question.id, value)} className={`p-3 rounded-lg border-2 transition-all ${responses[question.id] === value ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-blue-300 hover:bg-gray-50'}`} data-id="element-1557">
                      <div className="text-center" data-id="element-1558">
                        <div className="text-lg font-semibold text-gray-900 mb-1" data-id="element-1559">
                          {value}
                        </div>
                        <div className="text-xs text-gray-600" data-id="element-1560">
                          {scaleLabels[value - 1]}
                        </div>
                      </div>
                    </button>)}
                </div>
                <div data-id="element-1561">
                  <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-1562">
                    Commentaire (optionnel)
                  </label>
                  <textarea value={comments[question.id] || ''} onChange={e => handleComment(question.id, e.target.value)} placeholder="Ajoutez un commentaire pour préciser votre réponse..." rows={2} className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 text-sm" data-id="element-1563" />
                </div>
              </div>
            </div>)}
        </div>
      </div>

      <div className="flex justify-between items-center" data-id="element-1564">
        <button onClick={handlePrevious} disabled={currentCategoryIndex === 0} className={`flex items-center px-4 py-2 rounded-md font-medium ${currentCategoryIndex === 0 ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}`} data-id="element-1565">
          <ChevronLeft className="h-4 w-4 mr-1" data-id="element-1566" />
          Précédent
        </button>
        <div className="text-sm text-gray-600" data-id="element-1567">
          {currentCategory.questions.filter(q => responses[q.id] !== undefined).length}{' '}
          / {currentCategory.questions.length} réponses dans cette section
        </div>
        {currentCategoryIndex === categories.length - 1 ? <button onClick={handleSubmit} disabled={answeredQuestions !== totalQuestions} className={`flex items-center px-6 py-2 rounded-md font-medium ${answeredQuestions === totalQuestions ? 'bg-green-600 text-white hover:bg-green-700' : 'bg-gray-100 text-gray-400 cursor-not-allowed'}`} data-id="element-1568">
            <CheckCircle className="h-4 w-4 mr-1" data-id="element-1569" />
            Soumettre le sondage
          </button> : <button onClick={handleNext} className="flex items-center px-4 py-2 rounded-md font-medium bg-blue-600 text-white hover:bg-blue-700" data-id="element-1570">
            Suivant
            <ChevronRight className="h-4 w-4 ml-1" data-id="element-1571" />
          </button>}
      </div>
    </div>;
}